import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { useToast } from '@/components/ui/Toast'
import { Badge } from '@/components/ui/Badge'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Sheet } from '@/components/ui/Sheet'
import { useProfile } from '@/auth/SessionProvider'
import {
  APPROVAL_MODE_LABELS, addCategory, createDepartment, getChurchSettings, listAllCategories, listAllDepartments,
  listApprovalPolicies, setApprovalPolicy, setCategoryArchived, setDepartmentRetired, updateChurchSettings, updateDepartment,
} from '@/data/admin'
import type { ApprovalMode } from '@/data/admin'
import { formatDay } from '@/lib/format'
import { formatKwacha, toInputText } from '@/lib/money'
import { lusakaDay } from '@/lib/periods'
import { amountOrZeroToNgwee, amountToNgwee, departmentFormSchema, nameSchema, policyFormSchema, settingsFormSchema } from './schemas'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')
const MODES = Object.keys(APPROVAL_MODE_LABELS) as ApprovalMode[]
const linkBtn = 'inline-flex min-h-[44px] shrink-0 items-center rounded-control px-3 text-body font-medium text-brand hover:bg-neutral-tint'

function Loading() {
  return <SkeletonRows rows={4} />
}
function Failed({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <div>
      <p role="alert" className="text-body text-bad">{errText(error)}</p>
      <Button variant="secondary" size="lg" className="mt-3" onClick={retry}>Try again</Button>
    </div>
  )
}

/** Central treasury: how approval works, the departments, the spending categories, church-wide settings. */
export function SettingsPage() {
  const canAct = useProfile().isAdmin
  const qc = useQueryClient()
  const toast = useToast()
  const done = (msg: string) => toast.success(msg)

  const policiesQ = useQuery({ queryKey: ['policies'], queryFn: listApprovalPolicies })
  const deptsQ = useQuery({ queryKey: ['all-departments'], queryFn: listAllDepartments })
  const catsQ = useQuery({ queryKey: ['all-categories'], queryFn: listAllCategories })
  const settingsQ = useQuery({ queryKey: ['church-settings'], queryFn: getChurchSettings })

  // ---------------- approval rule
  const current = policiesQ.data?.[0]
  const [mode, setMode] = useState<ApprovalMode | null>(null)
  const [threshold, setThreshold] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [pErrors, setPErrors] = useState<Record<string, string>>({})
  const [pError, setPError] = useState('')
  const chosenMode = mode ?? current?.mode ?? 'budgets_and_all_expenses'
  const chosenThreshold = threshold ?? (current && current.thresholdNgwee > 0 ? toInputText(current.thresholdNgwee) : '')
  const policyM = useMutation({ mutationFn: setApprovalPolicy })
  const changed = current !== undefined && (chosenMode !== current.mode || (chosenMode === 'budgets_and_expenses_over_threshold' && amountToNgwee(chosenThreshold) !== current.thresholdNgwee))

  const submitPolicy = async (e: FormEvent) => {
    e.preventDefault()
    if (policyM.isPending) return
    setPError('')
    const parsed = policyFormSchema.safeParse({ mode: chosenMode, threshold: chosenThreshold, note })
    if (!parsed.success) {
      const errs: Record<string, string> = {}
      for (const i of parsed.error.issues) errs[String(i.path[0])] ??= i.message
      setPErrors(errs)
      return
    }
    setPErrors({})
    try {
      await policyM.mutateAsync({ mode: parsed.data.mode, thresholdNgwee: amountToNgwee(parsed.data.threshold) ?? 0, note: parsed.data.note })
      for (const k of ['policies', 'waiting-budgets', 'waiting-expenses']) await qc.invalidateQueries({ queryKey: [k] })
      setMode(null); setThreshold(null); setNote('')
      done('The approval rule was changed. It applies from now on.')
    } catch (err) {
      setPError(errText(err))
    }
  }

  // ---------------- departments
  const [deptSheet, setDeptSheet] = useState<'add' | { id: string; name: string; code: string } | null>(null)
  const [dCode, setDCode] = useState('')
  const [dName, setDName] = useState('')
  const [dErrors, setDErrors] = useState<Record<string, string>>({})
  const [dError, setDError] = useState('')
  const addDeptM = useMutation({ mutationFn: createDepartment })
  const renameM = useMutation({ mutationFn: (v: { id: string; name: string; code: string }) => updateDepartment(v.id, { name: v.name, code: v.code }) })
  const retireM = useMutation({ mutationFn: (v: { id: string; retire: boolean }) => setDepartmentRetired(v.id, v.retire) })
  const [retireTarget, setRetireTarget] = useState<{ id: string; name: string; retire: boolean } | null>(null)
  const [retireError, setRetireError] = useState('')
  const confirmRetire = async () => {
    if (!retireTarget || retireM.isPending) return
    setRetireError('')
    try {
      await retireM.mutateAsync({ id: retireTarget.id, retire: retireTarget.retire })
      for (const k of ['all-departments', 'departments']) await qc.invalidateQueries({ queryKey: [k] })
      done(retireTarget.retire ? `${retireTarget.name} was retired. Its records are all still there.` : `${retireTarget.name} is back in use.`)
      setRetireTarget(null)
    } catch (err) {
      setRetireError(errText(err))
    }
  }
  const openDept = (t: NonNullable<typeof deptSheet>) => {
    setDCode(t === 'add' ? '' : t.code); setDName(t === 'add' ? '' : t.name); setDErrors({}); setDError(''); setDeptSheet(t)
  }
  const submitDept = async (e: FormEvent) => {
    e.preventDefault()
    if (!deptSheet || addDeptM.isPending || renameM.isPending) return
    setDError('')
    if (deptSheet === 'add') {
      const p = departmentFormSchema.safeParse({ code: dCode, name: dName })
      if (!p.success) {
        const errs: Record<string, string> = {}
        for (const i of p.error.issues) errs[String(i.path[0])] ??= i.message
        return setDErrors(errs)
      }
      setDErrors({})
      try {
        await addDeptM.mutateAsync(p.data)
        for (const k of ['all-departments', 'departments']) await qc.invalidateQueries({ queryKey: [k] })
        setDeptSheet(null)
        done(`${p.data.name} was added. Now give someone a role in it from People.`)
      } catch (err) {
        setDError(errText(err))
      }
    } else {
      const p = departmentFormSchema.safeParse({ code: dCode, name: dName })
      if (!p.success) {
        const errs: Record<string, string> = {}
        for (const i of p.error.issues) errs[String(i.path[0])] ??= i.message
        return setDErrors(errs)
      }
      setDErrors({})
      try {
        await renameM.mutateAsync({ id: deptSheet.id, name: p.data.name, code: p.data.code })
        for (const k of ['all-departments', 'departments']) await qc.invalidateQueries({ queryKey: [k] })
        setDeptSheet(null)
        done('The department was updated.')
      } catch (err) {
        setDError(errText(err))
      }
    }
  }

  // ---------------- categories
  const [newCat, setNewCat] = useState('')
  const [catError, setCatError] = useState('')
  const addCatM = useMutation({ mutationFn: addCategory })
  const archiveM = useMutation({ mutationFn: (v: { id: string; archived: boolean }) => setCategoryArchived(v.id, v.archived) })
  const refreshCats = async () => {
    for (const k of ['all-categories', 'categories']) await qc.invalidateQueries({ queryKey: [k] })
  }
  const submitCat = async (e: FormEvent) => {
    e.preventDefault()
    if (addCatM.isPending) return
    const p = nameSchema.safeParse({ name: newCat })
    if (!p.success) return setCatError(p.error.issues[0].message)
    setCatError('')
    try {
      await addCatM.mutateAsync(p.data.name)
      await refreshCats()
      setNewCat('')
      done(`The category "${p.data.name}" was added.`)
    } catch (err) {
      setCatError(errText(err))
    }
  }
  const toggleCat = async (id: string, archived: boolean) => {
    setCatError('')
    try {
      await archiveM.mutateAsync({ id, archived })
      await refreshCats()
    } catch (err) {
      setCatError(errText(err))
    }
  }

  // ---------------- church settings
  const s = settingsQ.data
  const [sName, setSName] = useState<string | null>(null)
  const [sLimit, setSLimit] = useState<string | null>(null)
  const [sSla, setSSla] = useState<string | null>(null)
  const [sMatch, setSMatch] = useState<string | null>(null)
  const [sProofE, setSProofE] = useState<boolean | null>(null)
  const [sProofM, setSProofM] = useState<boolean | null>(null)
  const [sErrors, setSErrors] = useState<Record<string, string>>({})
  const [sError, setSError] = useState('')
  const settingsM = useMutation({ mutationFn: updateChurchSettings })
  const submitSettings = async (e: FormEvent) => {
    e.preventDefault()
    if (!s || settingsM.isPending) return
    setSError('')
    const v = {
      churchName: sName ?? s.churchName,
      cashLimit: sLimit ?? toInputText(s.deptCashFloatLimitNgwee),
      slaDays: sSla ?? String(s.remittanceSlaDays),
      matchDays: sMatch ?? String(s.automatchWindowDays),
    }
    const p = settingsFormSchema.safeParse(v)
    if (!p.success) {
      const errs: Record<string, string> = {}
      for (const i of p.error.issues) errs[String(i.path[0])] ??= i.message
      return setSErrors(errs)
    }
    setSErrors({})
    try {
      await settingsM.mutateAsync({
        ...(sName !== null ? { churchName: p.data.churchName } : {}),
        ...(sLimit !== null ? { deptCashFloatLimitNgwee: amountOrZeroToNgwee(p.data.cashLimit) as number } : {}),
        ...(sSla !== null ? { remittanceSlaDays: Number(p.data.slaDays) } : {}),
        ...(sMatch !== null ? { automatchWindowDays: Number(p.data.matchDays) } : {}),
        ...(sProofE !== null ? { requireProofOnExpense: sProofE } : {}),
        ...(sProofM !== null ? { requireProofOnMovement: sProofM } : {}),
      })
      await qc.invalidateQueries({ queryKey: ['church-settings'] })
      setSName(null); setSLimit(null); setSSla(null); setSMatch(null); setSProofE(null); setSProofM(null)
      done('The settings were saved.')
    } catch (err) {
      setSError(errText(err))
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-title font-semibold text-ink">Settings</h2>
        <p className="mt-1 text-muted">{canAct ? 'How approval works, the departments, spending categories and church-wide settings.' : 'You can look at these settings but not change them.'}</p>
      </div>

      <Card>
        <CardHeader><h3 className="font-semibold text-ink">Approval rule</h3></CardHeader>
        <CardBody>
          {policiesQ.isPending ? <Loading /> : policiesQ.isError ? <Failed error={policiesQ.error} retry={() => void policiesQ.refetch()} /> : (
            <form onSubmit={(e) => void submitPolicy(e)} className="flex flex-col gap-4" noValidate>
              <fieldset className="flex flex-col gap-2" disabled={!canAct}>
                <legend className="mb-1 text-body font-medium text-neutral">What needs central treasury&apos;s approval?</legend>
                {MODES.map((m) => (
                  <label key={m} className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-control border p-3 ${chosenMode === m ? 'border-brand bg-brand-tint' : 'border-line'}`}>
                    <input type="radio" name="mode" className="mt-1 h-5 w-5" checked={chosenMode === m} onChange={() => setMode(m)} />
                    <span>
                      <span className="block font-medium text-ink">{APPROVAL_MODE_LABELS[m].title}{current?.mode === m && <Badge tone="success" className="ml-2">In force now</Badge>}</span>
                      <span className="block text-body text-muted">{APPROVAL_MODE_LABELS[m].detail}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
              {chosenMode === 'budgets_and_expenses_over_threshold' && (
                <Input label="Expenses of this amount or more need approval (Kwacha)" inputMode="decimal" placeholder="1000" value={chosenThreshold} onChange={(e) => setThreshold(e.target.value)} error={pErrors.threshold} disabled={!canAct} />
              )}
              {canAct && changed && (
                <>
                  <Textarea label="Why are you changing it? (optional)" value={note} onChange={(e) => setNote(e.target.value)} error={pErrors.note} rows={2} />
                  <p className="rounded-control bg-wait-tint px-4 py-3 text-body text-wait">
                    The new rule applies from now on. Nothing already approved is undone. If the new rule is looser, anything waiting that no longer needs a decision is approved automatically.
                  </p>
                  {pError && <p role="alert" className="text-body text-bad">{pError}</p>}
                  <Button type="submit" size="lg" isLoading={policyM.isPending}>{policyM.isPending ? 'Saving...' : 'Change the rule'}</Button>
                </>
              )}
              {(policiesQ.data ?? []).length > 1 && (
                <details className="text-body text-neutral">
                  <summary className="min-h-[44px] cursor-pointer py-3 font-medium">Earlier rules</summary>
                  <ul className="divide-y divide-line">
                    {(policiesQ.data ?? []).slice(1).map((p) => (
                      <li key={p.id} className="py-2">
                        {APPROVAL_MODE_LABELS[p.mode].title}{p.thresholdNgwee > 0 ? ` (from ${formatKwacha(p.thresholdNgwee)})` : ''} · from {formatDay(lusakaDay(p.effectiveFrom))}
                        {p.note && <span className="block text-micro text-muted">{p.note}</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </form>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-ink">Departments</h3>
          {canAct && <Button size="lg" onClick={() => openDept('add')}>Add a department</Button>}
        </CardHeader>
        <CardBody>
          {deptsQ.isPending ? <Loading /> : deptsQ.isError ? <Failed error={deptsQ.error} retry={() => void deptsQ.refetch()} /> : (
            <ul className="divide-y divide-line">
              {(deptsQ.data ?? []).map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 py-2">
                  <span className="min-w-0">
                    <span className={d.archivedAt ? 'block font-medium text-muted line-through' : 'block font-medium text-ink'}>{d.name}</span>
                    <span className="block text-micro text-muted">{d.code}</span>
                  </span>
                  <span className="flex flex-wrap items-center justify-end gap-2">
                    {d.isCentral && <Badge tone="info">Central treasury</Badge>}
                    {d.archivedAt && <Badge tone="neutral">Retired</Badge>}
                    {canAct && <button type="button" className={linkBtn} onClick={() => openDept({ id: d.id, name: d.name, code: d.code })} aria-label={`Edit ${d.name}`}>Edit</button>}
                    {canAct && !d.isCentral && (
                      <button type="button" className={linkBtn} onClick={() => { setRetireError(''); setRetireTarget({ id: d.id, name: d.name, retire: !d.archivedAt }) }} aria-label={`${d.archivedAt ? 'Bring back' : 'Retire'} ${d.name}`}>
                        {d.archivedAt ? 'Bring back' : 'Retire'}
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader><h3 className="font-semibold text-ink">Spending categories</h3></CardHeader>
        <CardBody>
          {catsQ.isPending ? <Loading /> : catsQ.isError ? <Failed error={catsQ.error} retry={() => void catsQ.refetch()} /> : (
            <>
              <ul className="divide-y divide-line">
                {(catsQ.data ?? []).map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 py-1">
                    <span className={c.archivedAt ? 'text-muted line-through' : 'text-ink'}>{c.name}</span>
                    {canAct && (
                      <button type="button" className={linkBtn} onClick={() => void toggleCat(c.id, !c.archivedAt)}>
                        {c.archivedAt ? 'Show again' : 'Hide'}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {canAct && (
                <form onSubmit={(e) => void submitCat(e)} className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end" noValidate>
                  <div className="flex-1"><Input label="Add a category" value={newCat} onChange={(e) => setNewCat(e.target.value)} error={catError} /></div>
                  <Button type="submit" size="lg" isLoading={addCatM.isPending}>Add</Button>
                </form>
              )}
              <p className="mt-2 text-body text-muted">Hiding a category stops it being offered for new entries. Old expenses and budget lines keep it.</p>
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader><h3 className="font-semibold text-ink">Church settings</h3></CardHeader>
        <CardBody>
          {settingsQ.isPending ? <Loading /> : settingsQ.isError || !s ? <Failed error={settingsQ.error} retry={() => void settingsQ.refetch()} /> : (
            <form onSubmit={(e) => void submitSettings(e)} className="flex flex-col gap-4" noValidate>
              <fieldset className="flex flex-col gap-4" disabled={!canAct}>
                <Input label="Church name" value={sName ?? s.churchName} onChange={(e) => setSName(e.target.value)} error={sErrors.churchName} />
                <Input label="Most cash a department may hold (Kwacha)" inputMode="decimal" value={sLimit ?? toInputText(s.deptCashFloatLimitNgwee)} onChange={(e) => setSLimit(e.target.value)} error={sErrors.cashLimit} hint="Above this, central treasury is shown a warning." />
                <Input label="Days a department has to send cash on" type="number" inputMode="numeric" min={1} max={60} value={sSla ?? String(s.remittanceSlaDays)} onChange={(e) => setSSla(e.target.value)} error={sErrors.slaDays} />
                <Input label="Days apart a deposit and a remittance can be and still look alike" type="number" inputMode="numeric" min={0} max={30} value={sMatch ?? String(s.automatchWindowDays)} onChange={(e) => setSMatch(e.target.value)} error={sErrors.matchDays} />
                <label className="flex min-h-[44px] items-center gap-3 text-body font-medium text-neutral">
                  <input type="checkbox" className="h-5 w-5" checked={sProofE ?? s.requireProofOnExpense} onChange={(e) => setSProofE(e.target.checked)} />
                  Expect a receipt photo on every expense
                </label>
                <label className="flex min-h-[44px] items-center gap-3 text-body font-medium text-neutral">
                  <input type="checkbox" className="h-5 w-5" checked={sProofM ?? s.requireProofOnMovement} onChange={(e) => setSProofM(e.target.checked)} />
                  Expect a deposit slip photo on every remittance
                </label>
              </fieldset>
              {sError && <p role="alert" className="text-body text-bad">{sError}</p>}
              {canAct && <Button type="submit" size="lg" isLoading={settingsM.isPending}>{settingsM.isPending ? 'Saving...' : 'Save settings'}</Button>}
            </form>
          )}
        </CardBody>
      </Card>

      <Sheet isOpen={deptSheet !== null} onClose={() => setDeptSheet(null)} title={deptSheet === 'add' ? 'Add a department' : 'Edit the department'}>
        <form onSubmit={(e) => void submitDept(e)} className="flex flex-col gap-4" noValidate>
          <Input label="Short code" placeholder="WOMEN" value={dCode} onChange={(e) => setDCode(e.target.value)} error={dErrors.code} hint="Letters or numbers, no spaces. Only central treasury can change it." />
          <Input label="Name" placeholder="Women Fellowship" value={dName} onChange={(e) => setDName(e.target.value)} error={dErrors.name} />
          {dError && <p role="alert" className="text-body text-bad">{dError}</p>}
          <Button type="submit" size="lg" isLoading={addDeptM.isPending || renameM.isPending}>{deptSheet === 'add' ? 'Add department' : 'Save changes'}</Button>
        </form>
      </Sheet>

      <Sheet isOpen={retireTarget !== null} onClose={() => setRetireTarget(null)} title={retireTarget?.retire ? 'Retire this department?' : 'Bring this department back?'}>
        <div className="flex flex-col gap-4">
          <p className="text-neutral">
            {retireTarget?.retire
              ? `${retireTarget.name} will disappear from lists and no new entries can be made in it. Nothing is deleted: its members, payments, expenses and remittances stay on record and still count in church reports. You can bring it back at any time. Anyone who still has a role in it must be removed in People first.`
              : `${retireTarget?.name} will appear in lists again and can be used as before.`}
          </p>
          {retireError && <p role="alert" className="text-body text-bad">{retireError}</p>}
          <Button size="lg" onClick={() => void confirmRetire()} isLoading={retireM.isPending}>{retireTarget?.retire ? 'Retire department' : 'Bring it back'}</Button>
        </div>
      </Sheet>
    </div>
  )
}
